import { useState, useMemo } from "react";
import {
  User,
  MapPin,
  Package,
  Calendar,
  ScrollText,
  Plus,
  Search,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ScrollArea } from "@/client/components/ui/scroll-area";
import { Input } from "@/client/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/client/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/client/components/ui/select";
import { SortableCodexEntry, CodexEntryOverlay } from "./codex-entry";
import { FilesPanel } from "@/client/components/files/files-panel";
import {
  useCodex,
  useCreateCodexEntry,
  useDeleteCodexEntry,
  useReorderCodexEntries,
} from "@/client/hooks/use-codex";
import { useTabActions } from "@/client/hooks/use-tabs-context";
import type { CodexEntry as CodexEntryType } from "@/client/db";
import type { CodexType } from "@/client/hooks/use-codex";

interface CodexPanelProps {
  storyId: string;
  activeEntryId?: string | null;
}

const CODEX_TYPES: { type: CodexType; label: string; one: string; icon: typeof User }[] = [
  { type: "character", label: "Characters", one: "a character", icon: User },
  { type: "location", label: "Locations", one: "a location", icon: MapPin },
  { type: "object", label: "Objects", one: "an object", icon: Package },
  { type: "event", label: "Events", one: "an event", icon: Calendar },
  { type: "lore", label: "Lore", one: "a lore entry", icon: ScrollText },
];

export function CodexPanel({ storyId, activeEntryId }: CodexPanelProps) {
  const { openCodexTab } = useTabActions();
  const { data: entries = [], isLoading } = useCodex(storyId);
  const createEntry = useCreateCodexEntry();
  const deleteEntry = useDeleteCodexEntry();
  const reorderEntries = useReorderCodexEntries();

  const [searchQuery, setSearchQuery] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [view, setView] = useState<"codex" | "files">("codex");

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before starting drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Group entries by type and sort by order
  const groupedEntries = useMemo(() => {
    // Fuzzy search - checks if all characters in query appear in order in target
    const fuzzyMatch = (query: string, target: string): boolean => {
      const lowerQuery = query.toLowerCase();
      const lowerTarget = target.toLowerCase();
      
      let queryIndex = 0;
      for (let i = 0; i < lowerTarget.length && queryIndex < lowerQuery.length; i++) {
        if (lowerTarget[i] === lowerQuery[queryIndex]) {
          queryIndex++;
        }
      }
      return queryIndex === lowerQuery.length;
    };

    // Check if entry matches fuzzy search across name, tags, and content
    const matchesSearch = (entry: CodexEntryType, query: string): boolean => {
      if (fuzzyMatch(query, entry.name)) return true;
      if (entry.content && fuzzyMatch(query, entry.content)) return true;
      if (entry.tags?.some((tag) => fuzzyMatch(query, tag))) return true;
      return false;
    };

    const groups: Record<CodexType, CodexEntryType[]> = {
      character: [],
      location: [],
      object: [],
      event: [],
      lore: [],
    };

    const filtered = searchQuery
      ? entries.filter((e) => matchesSearch(e, searchQuery))
      : entries;

    filtered.forEach((entry) => {
      groups[entry.type].push(entry);
    });

    // Sort each group by order
    for (const type of Object.keys(groups) as CodexType[]) {
      groups[type].sort((a, b) => a.order - b.order);
    }

    return groups;
  }, [entries, searchQuery]);

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      // Find the type of the dragged entry
      const draggedEntry = entries.find((e) => e.id === active.id);
      if (draggedEntry) {
        const typeEntries = groupedEntries[draggedEntry.type];
        reorderEntries(typeEntries, active.id as string, over.id as string);
      }
    }
  };

  // Get the active entry for drag overlay
  const activeDragEntry = activeId ? entries.find((e) => e.id === activeId) : null;

  // Create a new entry and open it in a tab
  const handleAdd = async (type: CodexType) => {
    const newEntry = await createEntry.mutateAsync({
      storyId,
      data: {
        name: `New ${type.charAt(0).toUpperCase() + type.slice(1)}`,
        type,
      },
    });
    if (newEntry) {
      openCodexTab(newEntry.id, newEntry.name, newEntry.type);
    }
  };

  // Open existing entry in a tab
  const handleOpen = (entry: CodexEntryType) => {
    openCodexTab(entry.id, entry.name, entry.type);
  };

  const handleDelete = async (entry: CodexEntryType) => {
    await deleteEntry.mutateAsync({ id: entry.id, storyId });
  };

  // Get types that have entries for default open state
  const typesWithEntries = CODEX_TYPES.filter(
    ({ type }) => groupedEntries[type].length > 0
  ).map(({ type }) => type);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div data-region="panel-header" className="p-3 border-b">
        <Select value={view} onValueChange={(v) => setView(v as "codex" | "files")}>
          <SelectTrigger className="w-auto h-7 text-sm font-semibold border-0 shadow-none p-0 focus:ring-0 gap-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="codex">Codex</SelectItem>
            <SelectItem value="files">Files</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {view === "codex" ? (
        <>
          <div data-region="panel-search" className="px-3 pb-3 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search entries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
          </div>

          <ScrollArea className="flex-1 min-h-0">
            <div className="w-full overflow-hidden">
              {isLoading ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  Loading...
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <Accordion
                    type="multiple"
                    defaultValue={typesWithEntries}
                    className="px-2 py-1"
                  >
                    {CODEX_TYPES.map(({ type, label, one, icon: Icon }) => (
                      <AccordionItem key={type} value={type} className="border-b-0">
                        {/* The add button sits over a spacer in the header rather than
                            inside it: the header is a button, and buttons can't nest */}
                        <div className="relative">
                          <AccordionTrigger style={{ alignItems: 'center' }} className="py-2 hover:no-underline gap-1">
                            <div className="flex items-center gap-2 text-sm">
                              <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                              <span>{label}</span>
                            </div>
                            <div className="flex items-center gap-1 ml-auto shrink-0">
                              <span className="text-xs text-muted-foreground text-right">
                                {groupedEntries[type].length}
                              </span>
                              <span className="h-6 w-6 shrink-0" aria-hidden />
                            </div>
                          </AccordionTrigger>
                          <button
                            type="button"
                            aria-label={`Add ${one}`}
                            title={`Add ${one}`}
                            className="absolute right-5 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded hover:bg-accent cursor-pointer focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                            onClick={() => handleAdd(type)}
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <AccordionContent className="pb-2">
                          <SortableContext
                            items={groupedEntries[type].map((e) => e.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="space-y-1">
                              {groupedEntries[type].map((entry) => (
                                <SortableCodexEntry
                                  key={entry.id}
                                  entry={entry}
                                  onOpen={() => handleOpen(entry)}
                                  onDelete={() => handleDelete(entry)}
                                  isActive={activeEntryId === entry.id}
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>

                  {/* Drag overlay - shows a preview while dragging */}
                  <DragOverlay>
                    {activeDragEntry ? (
                      <CodexEntryOverlay entry={activeDragEntry} />
                    ) : null}
                  </DragOverlay>
                </DndContext>
              )}
            </div>
          </ScrollArea>
        </>
      ) : (
        <FilesPanel storyId={storyId} />
      )}
    </div>
  );
}
